"use client";

import { useState, useEffect } from "react";
import { Modal, ModalContent, ModalHeader, ModalBody, Input, Button } from "@nextui-org/react";
import { Loader2, MapPin, Search } from "lucide-react";

interface Location {
  name: string;
  address: string;
  coordinates: {
    lat: number;
    lng: number;
  };
}

interface CreateLocationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectLocation: (location: Location) => void;
}

const CreateLocationModal = ({ open, onOpenChange, onSelectLocation }: CreateLocationModalProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [currentLocation, setCurrentLocation] = useState<Location | null>(null);
  const [searchResults, setSearchResults] = useState<Location[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const getCurrentLocation = () => {
    setIsLoading(true);
    if (navigator.geolocation) {
      navigator?.geolocation?.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          // Reverse geocode to get address
          fetch(`https://nominatim?.openstreetmap?.org/reverse?format=json&lat=${latitude}&lon=${longitude}`)
            .then(response => response.json())
            .then(data => {
              const city = data?.address?.city || data?.address?.town || data?.address?.village || data?.address?.county;
              const location = {
                name: city,
                address: city,
                coordinates: {
                  lat: latitude,
                  lng: longitude
                }
              };
              setCurrentLocation(location);
              setIsLoading(false);
            })
            .catch(() => {
              setIsLoading(false);
            });
        },
        () => {
          setIsLoading(false);
        }
      );
    } else {
      setIsLoading(false);
    }
  };

  const handleSearch = () => {
    if (searchQuery.trim()) {
      setIsLoading(true);
      fetch(`https://nominatim?.openstreetmap?.org/search?format=json&q=${encodeURIComponent(searchQuery)}`)
        .then(response => response.json())
        .then(data => {
          const results = data?.map((item: any) => ({
            name: item.display_name,
            address: item.display_name,
            coordinates: {
              lat: parseFloat(item.lat),
              lng: parseFloat(item.lon)
            }
          }));
          setSearchResults(results);
          setIsLoading(false);
        })
        .catch(() => {
          setIsLoading(false);
        });
    }
  };

  useEffect(() => {
    if (open) {
      setSearchQuery("");
      setCurrentLocation(null);
      setSearchResults([]);
    }
  }, [open]);

  return (
    <Modal 
      isOpen={open} 
      onOpenChange={onOpenChange}
      size="2xl"
      className="bg-secondary"
    >
      <ModalContent>
        <ModalHeader className="text-xl">Tag a Location</ModalHeader>
        <ModalBody className="pb-6">
          <div className="space-y-4">
            <div className="flex gap-2">
              <Input
                placeholder="Search for a location..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                variant="bordered"
                classNames={{
                  input: "bg-transparent"
                }}
              />
              <Button
                isIconOnly
                onPress={handleSearch}
                isDisabled={isLoading}
                color="primary"
              >
                {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              </Button>
            </div>

            <Button
              variant="bordered"
              className="w-full"
              onPress={getCurrentLocation}
              isDisabled={isLoading}
              startContent={isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <MapPin className="h-4 w-4" />}
            >
              Use Current Location
            </Button>

            {(currentLocation || searchResults.length > 0) && (
              <div className="space-y-2">
                {currentLocation && (
                  <div
                    className="p-3 border border-secondary rounded-lg cursor-pointer hover:bg-secondary/10"
                    onClick={() => {
                      onSelectLocation(currentLocation);
                      onOpenChange(false);
                    }}
                  >
                    <div className="font-medium">Current Location</div>
                    <div className="text-sm text-secondary">{currentLocation.address}</div>
                  </div>
                )}

                {searchResults?.map((result, index) => (
                  <div
                    key={index}
                    className="p-3 border border-secondary rounded-lg cursor-pointer hover:bg-secondary/10"
                    onClick={() => {
                      onSelectLocation(result);
                      onOpenChange(false);
                    }}
                  >
                    <div className="font-medium">{result.name}</div>
                    <div className="text-sm text-secondary">{result.address}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default CreateLocationModal;